import { Close } from '@mui/icons-material';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    TextField,
    Autocomplete,
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDialog from '../hooks/useDialog';
import useErrors from '../hooks/useErrors';
import server from '../services/server';
import SelectAdminPlans from '../components/SelectAdminPlans';
import { useFetch } from '../hooks/useFecth';

const route = {
    advertiser: 'anunciantes',
    club: 'clubes',
};

function SelectClubs({ onChange, name, value }) {
    const { data, isFetching } = useFetch('migration/clubs');

    const selected = data?.find(item => Number(value) === item.numg_clube) || null;
    return (
        <Autocomplete
            placeholder="Selecione um clube"
            getOptionLabel={e => e?.sigl_clube || null}
            options={data || []}
            value={selected || null}
            name={name}
            loading={isFetching}
            onChange={(e, newValue) => onChange(name, newValue?.numg_clube)}
            renderInput={params => <TextField {...params} label="Selecione um clube" />}
        />
    );
}

function SelectAdvertisers({ onChange, name, value }) {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setLoadError(null);
            try {
                const { data } = await server.get('migration/advertiser');
                const raw = Array.isArray(data?.data) ? data.data : [];
                if (cancelled) return;
                setOptions(
                    raw
                        .map(item => {
                            const id = item.numg_pessoa ?? item.numg_conveniado ?? item.id;
                            const label = item.nome_pessoa ?? item.desc_conveniado ?? item.name ?? item.label;
                            if (id == null || !label) return null;
                            return { id: Number(id), label: String(label) };
                        })
                        .filter(Boolean)
                        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
                );
            } catch (err) {
                if (cancelled) return;
                setOptions([]);
                setLoadError(
                    err?.response?.data?.message ||
                        'Não foi possível carregar os anunciantes do legado. Verifique o serviço de migração.',
                );
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const selected = options.find(item => Number(value) === item.id) || null;

    return (
        <Stack spacing={1}>
            {loadError ? (
                <Alert severity="warning">{loadError}</Alert>
            ) : null}
            <Autocomplete
                fullWidth
                options={options}
                value={selected}
                loading={loading}
                disabled={Boolean(loadError)}
                filterSelectedOptions
                isOptionEqualToValue={(option, optionValue) => option?.id === optionValue?.id}
                getOptionLabel={option => (option?.label ? `${option.label} (#${option.id})` : '')}
                filterOptions={(opts, state) => {
                    const q = state.inputValue.trim().toLowerCase();
                    if (!q) return opts;
                    return opts.filter(
                        opt =>
                            opt.label.toLowerCase().includes(q) ||
                            String(opt.id).includes(q),
                    );
                }}
                onChange={(_e, newValue) => onChange(name, newValue?.id ?? '')}
                renderInput={params => (
                    <TextField
                        {...params}
                        label="Buscar anunciante legado"
                        placeholder="Digite o nome ou ID"
                        helperText="Pesquise pelo nome no sistema legado. O ID é preenchido automaticamente."
                    />
                )}
                noOptionsText={loading ? 'Carregando…' : 'Nenhum anunciante encontrado'}
            />
        </Stack>
    );
}

function Form({ mutate, clubId, club }) {
    const { setErrors, enqueueSnackbar } = useErrors();
    const { id: from, open, handleClose } = useDialog('#migration');
    const navigation = useNavigate();
    const { values, isSubmitting, setFieldValue, resetForm, handleSubmit } = useFormik({
        initialValues: {
            id: '',
            planId: '',
        },
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                const { data } = await server.post(`migration/${from}`, { id: values?.id, planId: values?.planId, clubId: club?.id });
                enqueueSnackbar(data.message);
                mutate();
                resetForm();
                if (['advertiser', 'club'].includes(from)) {
                    navigation(`/dashboard/${route[from]}/${data.data.id}`, { replace: true });
                }
                setSubmitting(false);
            } catch (error) {
                resetForm();
                setSubmitting(false);
                setErrors(error);
                console.log(error);
            }
        },
    });

    return (
        <>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
                <Stack direction="row" justifyContent="space-between" alignItems="center" marginRight={2}>
                    <DialogTitle>Migração</DialogTitle>
                    {/* <IconButton size="small" onClick={e => openHelp('offer_avaliableQty')}>
                            <Help />
                        </IconButton> */}
                    <IconButton size="small" onClick={handleClose}>
                        <Close />
                    </IconButton>
                </Stack>
                <Divider />
                <DialogContent>
                    <Stack rowGap={2}>
                        {['club'].includes(from) && (
                            <>
                                <SelectClubs value={values.id} name="id" onChange={setFieldValue} />
                                {from === 'club' && <SelectAdminPlans value={values.planId} name="planId" onChange={setFieldValue} />}
                            </>
                        )}

                        {['advertiser'].includes(from) && (
                            <SelectAdvertisers value={values.id} name="id" onChange={setFieldValue} />
                        )}

                        {/* {['subscriber'].includes(from) && (
                            <>
                                <Alert severity="info">Todos os assinantes serão migrados. Lembre-se de informar a mudança para os assinantes.</Alert>
                                {Array.isArray(club.functions) && club.functions.includes('reseller') && (
                                    <SelectClubPlans
                                        value={values.planId}
                                        name="planId"
                                        onChange={setFieldValue}
                                        placeholder="Selecione um plano para os usuários"
                                    />
                                )}
                            </>
                        )} */}
                    </Stack>
                </DialogContent>
                <Divider />
                <DialogActions>
                    <Button variant="contained" disabled={isSubmitting} onClick={handleSubmit}>
                        Migrar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
export default function DialogMigrate({ mutate, clubId, club }) {
    const { open } = useDialog('#migration');

    if (open) return <Form mutate={mutate} clubId={clubId} club={club} />;
}
